import {
  SkillOverrideSourceEnum,
  SkillVisibilityEnum,
} from "types/graphql-schema";

import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import Link from "@components/link/link";
import { classNames } from "@helpers/css";

export type SkillCardData = {
  id: string | null;
  name: string;
  slug: string;
  description: string;
  body: string;
  canEdit: boolean;
  canArchive: boolean;
  visibility: SkillVisibilityEnum;
  overridesSource: SkillOverrideSourceEnum;
  isScheduled: boolean;
};

const pillClassName =
  "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium";

const sourceLabel = (visibility: SkillVisibilityEnum): string => {
  switch (visibility) {
    case SkillVisibilityEnum.Workspace:
      return "Workspace";
    case SkillVisibilityEnum.Platform:
      return "Platform";
    default:
      return "Personal";
  }
};

const sourcePillClass = (visibility: SkillVisibilityEnum): string => {
  switch (visibility) {
    case SkillVisibilityEnum.Workspace:
      return "bg-purple-100 text-purple-700";
    case SkillVisibilityEnum.Platform:
      return "bg-gray-100 text-gray-700";
    default:
      return "bg-blue-100 text-blue-700";
  }
};

const SkillCard = ({
  skill,
  actions,
  onClick,
}: {
  skill: SkillCardData;
  actions: DropdownOptionType[];
  onClick?: () => void;
}) => (
  <div
    aria-label={`Skill row ${skill.name}`}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    className={classNames(
      "flex items-start justify-between gap-2 p-3 bg-white border border-gray-200 rounded-md",
      onClick && "cursor-pointer hover:bg-gray-50",
    )}
  >
    <div className="min-w-0 flex-1">
      <div className="tf-header-sm-dark">{skill.slug}</div>
      {skill.description && (
        <div className="text-sm text-gray-600 mt-0.5">{skill.description}</div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        <span
          className={classNames(
            pillClassName,
            sourcePillClass(skill.visibility),
          )}
        >
          {sourceLabel(skill.visibility)}
        </span>
        {skill.overridesSource !== SkillOverrideSourceEnum.None && (
          <span
            className={classNames(pillClassName, "bg-amber-100 text-amber-700")}
          >
            {skill.overridesSource === SkillOverrideSourceEnum.Workspace
              ? "Overrides workspace"
              : "Overrides platform"}
          </span>
        )}
        {skill.isScheduled && (
          <Link
            to="/agent/routines"
            onClick={(e) => e.stopPropagation()}
            className={classNames(
              pillClassName,
              "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
            )}
          >
            Scheduled
          </Link>
        )}
      </div>
    </div>
    <Dropdown
      options={actions}
      aria-label={`Skill actions for ${skill.name}`}
    />
  </div>
);

export default SkillCard;
